import { type ReactNode } from 'react';
import { buildGroupedLayers } from '../controls/buildGroupedLayers.ts';
import { type IMapGroup } from '../MapGroup.ts';
import { type IMapLayer } from '../MapLayer.ts';
import {
  getGroupNodeKey,
  getLayerNodeKey,
  UNGROUPED_NODE_KEY,
} from './mapLayersTreeKeys.ts';

/** Nodo dell'albero gruppi/layer nel formato atteso da Ant `Tree`. */
export interface IMapLayersTreeNode {
  title: ReactNode;
  key: string;
  isLeaf?: boolean;
  children?: IMapLayersTreeNode[];
}

/** Opzioni di costruzione dei nodi dell'albero. */
export interface IBuildMapLayersTreeDataOptions {
  /** Personalizza il titolo della foglia layer (default: `layer.name`). */
  renderLayerTitle?: (layer: IMapLayer) => ReactNode;
  /**
   * Personalizza il titolo del nodo gruppo. Riceve il gruppo e la label già
   * formattata (con eventuale conteggio); default: la label stessa.
   */
  renderGroupTitle?: (group: IMapGroup, label: string) => ReactNode;
  /** Mostra il conteggio dei livelli accanto al nome del gruppo. */
  showCounts?: boolean;
  /** Etichetta della sezione dei livelli senza gruppo. */
  ungroupedLabel?: string;
}

function countLabel(count: number): string {
  return `${count} ${count === 1 ? 'livello' : 'livelli'}`;
}

function groupLabel(
  group: IMapGroup,
  count: number,
  showCounts: boolean,
): string {
  const base = group.label || group.name;
  return showCounts ? `${base} (${countLabel(count)})` : base;
}

/**
 * Costruisce i nodi per il componente {@link MapLayersTreeInput} a
 * partire da gruppi e layer. Riusa {@link buildGroupedLayers} per l'ordine dei
 * gruppi e la raccolta dei livelli senza gruppo.
 *
 * - Se non ci sono gruppi, i layer sono foglie a livello radice (elenco piatto).
 * - Altrimenti ogni gruppo è un nodo padre (anche se vuoto) e i livelli senza
 *   gruppo confluiscono in un nodo virtuale.
 *
 * Le chiavi dei nodi sono ancorate a `code`/`name` (vedi `mapLayersTreeKeys`).
 */
export function buildMapLayersTreeData(
  groups: IMapGroup[],
  layers: IMapLayer[],
  options: IBuildMapLayersTreeDataOptions = {},
): IMapLayersTreeNode[] {
  const {
    renderLayerTitle,
    renderGroupTitle,
    showCounts = false,
    ungroupedLabel,
  } = options;

  const grouped = buildGroupedLayers(layers, groups);

  const leaf = (layer: IMapLayer): IMapLayersTreeNode => ({
    title: renderLayerTitle ? renderLayerTitle(layer) : layer.name,
    key: getLayerNodeKey(layer.code),
    isLeaf: true,
  });

  // Nessun gruppo → elenco piatto di foglie.
  if (groups.length === 0) {
    return layers.map(leaf);
  }

  const { sortedGroups, groupedIndices, ungroupedIndices } = grouped;

  const groupNodes = sortedGroups.map(group => {
    const indices = groupedIndices[group.name] ?? [];
    const label = groupLabel(group, indices.length, showCounts);
    return {
      title: renderGroupTitle ? renderGroupTitle(group, label) : label,
      key: getGroupNodeKey(group.name),
      children: indices.map(index => leaf(layers[index])),
    };
  });

  if (ungroupedIndices.length === 0) {
    return groupNodes;
  }

  const base = ungroupedLabel ?? 'Livelli senza gruppo';
  return [
    ...groupNodes,
    {
      title: showCounts
        ? `${base} (${countLabel(ungroupedIndices.length)})`
        : base,
      key: UNGROUPED_NODE_KEY,
      children: ungroupedIndices.map(index => leaf(layers[index])),
    },
  ];
}
